import { PageTitle } from '@ospace/layout'
import { ConfirmationModal, showNotification } from '@ospace/shared'
import { config } from '@ospace/shared'
import { Group, Loader, Pagination, Tabs, Text, Button } from '@mantine/core'
import { IconPlayerPlay } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { SearchFilters, type SearchFilterValues } from '../components/SearchFilters'
import { SearchResultsTable } from '../components/SearchResultsTable'
import { RunsTable } from '../components/RunsTable'
import { deduplicationKindConfig, type DeduplicationKind } from '../config'
import { useDeduplicationRuns } from '../hooks/useDeduplicationRuns'
import { useDeduplicationSearch } from '../hooks/useDeduplicationSearch'
import { useStartDeduplicationRun } from '../hooks/useStartDeduplicationRun'
import type { SearchQueryParams } from '../types'
import { observerPath } from '../utils/paths'

const PAGE_SIZE = 50

const readFiltersFromUrl = (params: URLSearchParams): SearchFilterValues => ({
  q: params.get('q') ?? '',
  action: params.get('action') ?? '',
  from: params.get('from') ?? '',
  to: params.get('to') ?? '',
  traceStatus: params.get('traceStatus') ?? '',
})

const buildSearchParams = (filters: SearchFilterValues, offset: number): SearchQueryParams => ({
  q: filters.q || undefined,
  action: filters.action || undefined,
  from: filters.from || undefined,
  to: filters.to || undefined,
  traceStatus: filters.traceStatus || undefined,
  limit: PAGE_SIZE,
  offset,
})

type Props = {
  kind: DeduplicationKind
}

export const ObserverPage = ({ kind }: Props) => {
  const kindConfig = deduplicationKindConfig[kind]
  const navigate = useNavigate()
  const { search } = useLocation()
  const urlParams = useMemo(() => new URLSearchParams(search), [search])
  const initialTab = urlParams.get('tab') === 'search' ? 'search' : 'runs'
  const [tab, setTab] = useState(initialTab)
  const [filters, setFilters] = useState<SearchFilterValues>(() => readFiltersFromUrl(urlParams))
  const [searchOffset, setSearchOffset] = useState(Number(urlParams.get('offset') ?? 0))
  const [runsOffset, setRunsOffset] = useState(Number(urlParams.get('runsOffset') ?? 0))
  const [searchSubmitted, setSearchSubmitted] = useState(
    initialTab === 'search' &&
      (urlParams.has('q') ||
        urlParams.has('action') ||
        urlParams.has('from') ||
        urlParams.has('to') ||
        urlParams.has('traceStatus'))
  )

  const syncUrl = (
    nextTab: string,
    nextFilters: SearchFilterValues,
    nextSearchOffset: number,
    nextRunsOffset: number
  ) => {
    const next = new URLSearchParams()
    next.set('tab', nextTab)
    if (nextFilters.q) next.set('q', nextFilters.q)
    if (nextFilters.action) next.set('action', nextFilters.action)
    if (nextFilters.from) next.set('from', nextFilters.from)
    if (nextFilters.to) next.set('to', nextFilters.to)
    if (nextFilters.traceStatus) next.set('traceStatus', nextFilters.traceStatus)
    if (nextSearchOffset > 0) next.set('offset', String(nextSearchOffset))
    if (nextRunsOffset > 0) next.set('runsOffset', String(nextRunsOffset))
    navigate({ pathname: kindConfig.basePath, search: next.toString() }, { replace: true })
  }

  const searchQuery = buildSearchParams(filters, searchOffset)
  const {
    data: searchData,
    isFetching: searchLoading,
    status: searchStatus,
  } = useDeduplicationSearch(kind, searchQuery, searchSubmitted)
  const { data: runsData, isFetching: runsLoading } = useDeduplicationRuns(
    kind,
    PAGE_SIZE,
    runsOffset
  )
  const startRun = useStartDeduplicationRun()
  const hasRunningRun = runsData?.results.some((run) => run.status === 'running') ?? false
  const manualRunsEnabled =
    kindConfig.manualRunsEnabled && config.deduplicationApiGateway.isConfigured

  const handleSearch = () => {
    setSearchOffset(0)
    setSearchSubmitted(true)
    syncUrl(tab, filters, 0, runsOffset)
  }

  const searchTotalPages = searchData ? Math.ceil(searchData.total / PAGE_SIZE) : 0
  const runsTotalPages = runsData ? Math.ceil(runsData.total / PAGE_SIZE) : 0

  return (
    <>
      <Group justify='space-between' mb='md' wrap='wrap'>
        <PageTitle>{kindConfig.title}</PageTitle>
        <Group>
          {manualRunsEnabled ? (
            <ConfirmationModal
              alertMessage={
                <Text size='sm'>
                  {kindConfig.runDescription}
                  <br />
                  <br />
                  The run may take several minutes. You can monitor progress on the Runs tab.
                </Text>
              }
              isLoading={startRun.isLoading}
              action={async () => {
                try {
                  const result = await startRun.mutateAsync(kindConfig.objectType)
                  setTab('runs')
                  setRunsOffset(0)
                  syncUrl('runs', filters, searchOffset, 0)
                  showNotification({
                    title: 'Run started',
                    message: result.local
                      ? result.message
                      : result.taskArn
                        ? `${result.message} (ECS ${result.taskStatus ?? 'starting'}: ${result.taskArn})`
                        : result.message,
                    type: 'success',
                  })
                } catch (error) {
                  showNotification({
                    title: 'Failed to start run',
                    message: error instanceof Error ? error.message : 'Unknown error',
                    type: 'error',
                  })
                }
              }}
              renderButton={({ onClick }) => (
                <Button
                  leftSection={<IconPlayerPlay size={16} />}
                  onClick={onClick}
                  loading={startRun.isLoading}
                  disabled={hasRunningRun}
                >
                  Run now
                </Button>
              )}
            />
          ) : null}
          {kindConfig.settingsPath ? (
            <Button component={Link} to={kindConfig.settingsPath} variant='light'>
              Settings
            </Button>
          ) : null}
          {kind !== 'company' ? (
            <Button component={Link} to={observerPath('company')} variant='subtle'>
              Companies
            </Button>
          ) : (
            <Button component={Link} to={observerPath('contact')} variant='subtle'>
              Contacts
            </Button>
          )}
        </Group>
      </Group>

      <Tabs
        value={tab}
        onChange={(value) => {
          const nextTab = value ?? 'runs'
          setTab(nextTab)
          syncUrl(nextTab, filters, searchOffset, runsOffset)
        }}
        mb='lg'
      >
        <Tabs.List>
          <Tabs.Tab value='runs'>Runs</Tabs.Tab>
          <Tabs.Tab value='search'>Search groups</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value='search' pt='md'>
          <SearchFilters
            values={filters}
            onChange={setFilters}
            onSearch={handleSearch}
            loading={searchLoading}
          />

          {!searchSubmitted ? (
            <Text c='dimmed'>Enter a search term or filter, then click Search.</Text>
          ) : searchLoading && !searchData ? (
            <Group justify='center' py='xl'>
              <Loader />
            </Group>
          ) : searchStatus === 'error' ? (
            <Text c='red'>Search failed. Check the API is deployed and you have admin access.</Text>
          ) : searchData ? (
            <>
              <SearchResultsTable
                kind={kind}
                results={searchData.results}
                total={searchData.total}
                isLoading={searchLoading}
              />
              {searchTotalPages > 1 && (
                <Group justify='center' mt='md'>
                  <Pagination
                    total={searchTotalPages}
                    value={Math.floor(searchOffset / PAGE_SIZE) + 1}
                    onChange={(page) => {
                      const nextOffset = (page - 1) * PAGE_SIZE
                      setSearchOffset(nextOffset)
                      syncUrl(tab, filters, nextOffset, runsOffset)
                    }}
                  />
                </Group>
              )}
            </>
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel value='runs' pt='md'>
          {runsLoading && !runsData ? (
            <Group justify='center' py='xl'>
              <Loader />
            </Group>
          ) : (
            <>
              <RunsTable kind={kind} runs={runsData?.results ?? []} isLoading={runsLoading} />
              {runsTotalPages > 1 && (
                <Group justify='center' mt='md'>
                  <Pagination
                    total={runsTotalPages}
                    value={Math.floor(runsOffset / PAGE_SIZE) + 1}
                    onChange={(page) => {
                      const nextOffset = (page - 1) * PAGE_SIZE
                      setRunsOffset(nextOffset)
                      syncUrl(tab, filters, searchOffset, nextOffset)
                    }}
                  />
                </Group>
              )}
            </>
          )}
        </Tabs.Panel>
      </Tabs>
    </>
  )
}
